import React, { useEffect, useState } from "react";
import "./DrSection.css";
import doctorImg from "../../assets/dr.jpeg"; // add your image
import { Link } from "react-router-dom";
import { fetchJson } from "../../api";

/**
 * "Meet Our Expert" home section.
 *
 * - 0 featured doctors  -> falls back to the first active doctor.
 * - 1 featured doctor   -> same static layout as before.
 * - 2+ featured doctors -> a carousel cycling through all of them:
 *    autoplay every 6s, pause on hover, prev/next arrows + dot navigation.
 *
 * No carousel library — the layout itself doesn't change, only which
 * doctor is displayed, so a keyed fade is all that's needed.
 */

const AUTOPLAY_MS = 6000;

const DoctorSection = () => {
  const [docs, setDocs] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let mounted = true;

    fetchJson("doctors").then((data) => {
      if (!mounted || !data || !Array.isArray(data.doctors)) return;

      const featured = data.doctors.filter((d) => d.featured);
      const list = (featured.length > 0 ? featured : data.doctors.slice(0, 1)).map((d) => ({
        id: d.id,
        name: d.name,
        role: d.designation || "",
        description: d.description || "",
        image: d.image_url || doctorImg,
        experience_years: d.experience_years || null,
        cases_count: d.cases_count || null,
      }));

      setDocs(list);
    });

    return () => {
      mounted = false;
    };
  }, []);

  const isCarousel = docs.length > 1;

  // Autoplay — only while carousel mode is active and not hovered.
  useEffect(() => {
    if (!isCarousel || paused || docs.length === 0) return undefined;

    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % docs.length);
    }, AUTOPLAY_MS);

    return () => clearInterval(timer);
  }, [isCarousel, paused, docs.length]);

  if (docs.length === 0) return null; // nothing in the DB yet -> render nothing

  const safeIndex = index % docs.length;
  const doc = docs[safeIndex];
  const go = (dir) => setIndex((i) => (i + dir + docs.length) % docs.length);

  return (
    <section
      className="doctor-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="container doctor-wrapper">

        {/* IMAGE */}
        <div className="doctor-image">
          <img src={doc.image} alt={doc.name} />
        </div>

        {/* CONTENT */}
        <div className="doctor-contentt">

          <span className="gynae-tag">
            {isCarousel ? `Meet Our Experts (${safeIndex + 1}/${docs.length})` : "Meet Our Expert"}
          </span>

          {/* key={doc.id} re-mounts on doctor change so the fade animation replays */}
          <div className="doctor-slide" key={doc.id}>
            <h2>{doc.name}</h2>

            <p className="doctor-role">{doc.role}</p>

            <p className="doctor-desc">{doc.description}</p>

            <div className="doctor-stats">
              {doc.experience_years && (
                <div>
                  <h3>{doc.experience_years}</h3>
                  <p>Years Experience</p>
                </div>
              )}
              {doc.cases_count && (
                <div>
                  <h3>{doc.cases_count}</h3>
                  <p>Successful Cases</p>
                </div>
              )}
            </div>
          </div>

          {/* <button className="doctor-btn">Book Consultation</button> */}
            <Link to="/bookappointment" className='about-btn'>Book Consultation</Link>

          {isCarousel && (
            <div className="doctor-carousel-nav">
              <button
                type="button"
                className="doctor-carousel-arrow"
                onClick={() => go(-1)}
                aria-label="Previous doctor"
              >
                ‹
              </button>

              <div className="doctor-carousel-dots" role="tablist" aria-label="Featured doctors">
                {docs.map((d, i) => (
                  <button
                    key={d.id}
                    type="button"
                    role="tab"
                    aria-selected={i === safeIndex}
                    aria-label={`Show ${d.name}`}
                    className={`doctor-carousel-dot ${i === safeIndex ? "active" : ""}`}
                    onClick={() => setIndex(i)}
                  />
                ))}
              </div>

              <button
                type="button"
                className="doctor-carousel-arrow"
                onClick={() => go(1)}
                aria-label="Next doctor"
              >
                ›
              </button>
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export default DoctorSection;
